import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Redirect, useParams, useLocation } from 'react-router-dom';
import config from '../../../config';
import { getProvider } from '../config/providers';
import useMarketplaceSession from '../hooks/useMarketplaceSession';
import { probe } from '../hooks/marketplaceSessionStore';
import safeNextPath from '../lib/nextPath';
import MarketplaceResolving from './MarketplaceResolving';
import '../marketplace.css';

export default function Connect() {
	const { provider: providerKey } = useParams();
	const provider = getProvider(providerKey);
	const location = useLocation();
	const session = useMarketplaceSession();

	const query = new URLSearchParams(location.search);
	const status = query.get('status');
	const fromMarketplace = query.get('ref') === provider.key;
	const autoConnect = query.get('autoConnect') === '1';

	const next = safeNextPath(query.get('next'));
	const justSignedIn = query.get('status') === 'signedin';

	const signInUrl = provider.signInStartPath ? `${config.partnerApiUrl}${provider.signInStartPath}` : null;
	const connectUrl = provider.oauthStartPath ? `${config.partnerApiUrl}${provider.oauthStartPath}` : null;
	const nextAuthUrl = session.signedIn ? connectUrl : signInUrl;

	const authParams = new URLSearchParams();

	if (!session.signedIn && autoConnect) authParams.set('autoConnect', '1');
	if (next) authParams.set('next', next);

	const authUrl = nextAuthUrl && (authParams.toString() ? `${nextAuthUrl}?${authParams}` : nextAuthUrl);

	const [redirecting, setRedirecting] = useState(false);
	const started = useRef(false);

	useEffect(() => {
		probe(providerKey);
	}, [providerKey]);

	useEffect(() => {
		if (!fromMarketplace || status || !session.confirmed || !authUrl) return;
		if (session.signedIn && session.connected) return;
		if (started.current) return;

		started.current = true;
		setRedirecting(true);
		window.location.replace(authUrl);
	}, [fromMarketplace, status, session.confirmed, session.signedIn, session.connected, authUrl]);

	if (status === 'email_unverified') {
		return (
			<>
				<Helmet>
					<meta name="referrer" content="no-referrer" />
					<title>Verify your Intuit email | Uplyft Capital</title>
				</Helmet>
				<main className="mp-page" role="main">
					<div className="container">
						<div className="mp-card">
							<div className="mp-icon">
								<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M12 9v4M12 17h.01" stroke="#927DFF" strokeWidth="1.8" strokeLinecap="round" />
									<circle cx="12" cy="12" r="9" stroke="#927DFF" strokeWidth="1.6" />
								</svg>
							</div>

							<h1>Verify your Intuit email address</h1>
							<p className="mp-subhead">
								Intuit hasn&apos;t confirmed the email address on your account yet, so we
								can&apos;t sign you in or connect {provider.label}. Verify it with Intuit,
								then come back and sign in again.
							</p>

							<div className="mp-buttons">
								<a
									href="https://accounts.intuit.com/app/account-manager/security"
									className="mp-cta"
									target="_blank"
									rel="noopener noreferrer"
								>
									Verify email with Intuit
								</a>
								<a href={`/marketplace/${provider.key}/connect`} className="mp-cta mp-cta-secondary">
									Try Again
								</a>
							</div>
						</div>
					</div>
				</main>
			</>
		);
	}

	if (!session.confirmed || redirecting) {
		return (
			<MarketplaceResolving
				provider={provider}
				message={
					redirecting
						? `Taking you to ${provider.label} to approve access…`
						: justSignedIn
							? 'Signing you in…'
							: `Checking your ${provider.label} connection…`
				}
			/>
		);
	}

	// connected
	if (session.signedIn && session.connected) {
		return <Redirect to={next || `/marketplace/${provider.key}/connected`} />;
	}

	const reconnecting = session.signedIn
		&& !session.connected
		&& (session.status === 'revoked' || session.status === 'expired');

	const oauthButton = session.signedIn ? provider.connectButtonImage : provider.signInButtonImage;
	const oauthButtonLabel = (oauthButton && oauthButton.label) || `Connect ${provider.label}`;

	const notice = status === 'disconnected'
		? `Your ${provider.label} connection was removed. You can connect again whenever you're ready.`
		: status === 'denied'
		? `The ${provider.label} authorization was cancelled before it finished.`
		: status === 'error'
		? `Something went wrong connecting ${provider.label}. Please try again.`
		: '';

	return (
		<>
			<Helmet>
				<title>Connect {provider.label} | Uplyft Capital</title>
			</Helmet>
			<main className="mp-page" role="main">
				<div className="container">
					<div className="mp-card">
						<div className={provider.logo ? 'mp-icon mp-icon--plain' : 'mp-icon'}>
							{provider.logo ? (
								<img src={provider.logo} alt={`${provider.label} logo`} className="mp-icon-img" />
							) : (
								<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M12 2L3 7v6c0 5 4 9 9 9s9-4 9-9V7l-9-5z" stroke="#927DFF" strokeWidth="1.6" fill="none" />
									<path d="M8.5 12.5l2.3 2.3 4.7-4.8" stroke="#927DFF" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							)}
						</div>

						<h1>
							{reconnecting
								? `Connect ${provider.label} to Uplyft Capital`
								: `Connect ${provider.label} to Uplyft Capital`}
						</h1>
						<p className="mp-subhead">
							{reconnecting
								? `Your ${provider.label} connection is no longer active. Reconnect to continue where you left off.`
								: <>Get fast funding offers from Uplyft &mdash; powered by your {provider.label} data.</>}
						</p>

						{notice && <p className="mp-notice" role="status">{notice}</p>}

						<div className="mp-trust-row">
							<div className="mp-trust-item">
								<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M12 2l7 3v6c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V5l7-3z" stroke="currentColor" strokeWidth="1.6" />
								</svg>
								<span>Secure, read-only access</span>
							</div>
							<div className="mp-trust-item">
								<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
									<path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
								</svg>
								<span>Takes under a minute</span>
							</div>
							<div className="mp-trust-item">
								<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
								<span>No obligation</span>
							</div>
						</div>

						{authUrl ? (
							oauthButton ? (
								<a
									href={authUrl}
									className="mp-cta-oauth-image"
									aria-label={oauthButton.label}
									style={{ width: oauthButton.width, height: oauthButton.height }}
								>
									<img
										src={oauthButton.default}
										alt={oauthButton.label}
										className="mp-cta-oauth-image__layer mp-cta-oauth-image__default"
									/>
									<img
										src={oauthButton.hover}
										alt=""
										aria-hidden="true"
										className="mp-cta-oauth-image__layer mp-cta-oauth-image__hover"
									/>
								</a>
							) : (
								<a
									href={authUrl}
									className="mp-cta"
									style={{ background: provider.brandColor }}
								>
									{oauthButtonLabel}
								</a>
							)
						) : (
							<p className="mp-subhead">This integration isn&apos;t available yet.</p>
						)}

						<p className="mp-fineprint">
							By connecting {provider.label}, you authorize Uplyft to securely view your accounting data
							to determine funding eligibility. You&apos;ll approve this on {provider.label}&apos;s site.
						</p>
					</div>
				</div>
			</main>
		</>
	);
}
